import React, { useState, useEffect } from 'react';
import { FaThumbsDown, FaThumbsUp, FaQuestion } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { EventParticipationAnswer } from '../../types/event';
import Icon from '../icons/Icon';
import { COLOR_ALERT, COLOR_ERROR, COLOR_SUCCESS } from '../../constants';

interface AttendanceComponentProps {
  selectedAnswer: EventParticipationAnswer | null;
  setSelectedAnswer: (ans: EventParticipationAnswer) => void;
  disableYes?: boolean;
}

export default function AttendanceComponent({
  selectedAnswer,
  setSelectedAnswer,
  disableYes = false,
}: AttendanceComponentProps) {
  const { t } = useTranslation();
  const [clickedButton, setClickedButton] = useState<EventParticipationAnswer | null>(null);

  useEffect(() => {
    if (clickedButton && clickedButton !== selectedAnswer) {
      const timer = setTimeout(() => {
        setClickedButton(null);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [clickedButton, selectedAnswer]);

  const handleButtonClick = (answer: EventParticipationAnswer) => {
    setSelectedAnswer(answer);
    setClickedButton(answer);
  };

  const getButtonText = (answer: EventParticipationAnswer) => {
    switch (answer) {
      case EventParticipationAnswer.YES:
        return t('page.events.infoScreen.attendance.yes');
      case EventParticipationAnswer.MAYBE:
        return t('page.events.infoScreen.attendance.maybe');
      case EventParticipationAnswer.NO:
        return t('page.events.infoScreen.attendance.no');
      default:
        return '';
    }
  };

  const shouldShowPill = (answer: EventParticipationAnswer) => {
    return selectedAnswer === answer || clickedButton === answer;
  };

  return (
    <div className="flex items-center justify-center gap-2 md:gap-4">
      {/* YES Button */}
      <div className="relative flex items-center">
        <div
          onClick={() => handleButtonClick(EventParticipationAnswer.YES)}
          className={`flex items-center transition-all duration-300 ${
            shouldShowPill(EventParticipationAnswer.YES) ? 'opacity-100' : 'absolute opacity-0'
          }`}>
          <button
            type="button"
            key={EventParticipationAnswer.YES}
            disabled={disableYes}
            className={`flex h-10 w-10 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 md:h-14 md:w-14 ${
              selectedAnswer === EventParticipationAnswer.YES ? 'shadow-lg' : 'hover:scale-105'
            } ${disableYes ? 'cursor-not-allowed opacity-50' : ''} ${
              shouldShowPill(EventParticipationAnswer.YES) ? 'rounded-r-none' : 'rounded-full'
            }`}
            style={{ backgroundColor: COLOR_SUCCESS }}>
            <Icon icon={FaThumbsUp} />
          </button>
          <div
            className={`flex h-10 items-center rounded-r-full px-3 text-sm font-medium text-white transition-all duration-300 md:h-14 md:px-4 md:text-base ${
              shouldShowPill(EventParticipationAnswer.YES)
                ? 'w-auto translate-x-0 opacity-70'
                : 'w-0 -translate-x-4 overflow-hidden opacity-0'
            }`}
            style={{ backgroundColor: COLOR_SUCCESS }}>
            {getButtonText(EventParticipationAnswer.YES)}
          </div>
        </div>
        <button
          type="button"
          key={`${EventParticipationAnswer.YES}-standalone`}
          onClick={() => handleButtonClick(EventParticipationAnswer.YES)}
          disabled={disableYes}
          className={`flex h-10 w-10 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 md:h-14 md:w-14 ${
            selectedAnswer === EventParticipationAnswer.YES ? 'shadow-lg' : 'hover:scale-105'
          } ${disableYes ? 'cursor-not-allowed opacity-50' : ''} ${
            shouldShowPill(EventParticipationAnswer.YES) ? 'absolute opacity-0' : 'opacity-100'
          }`}
          style={{ backgroundColor: COLOR_SUCCESS }}>
          <Icon icon={FaThumbsUp} />
        </button>
      </div>

      {/* MAYBE Button */}
      <div className="relative flex items-center">
        <div
          onClick={() => handleButtonClick(EventParticipationAnswer.MAYBE)}
          className={`flex items-center transition-all duration-300 ${
            shouldShowPill(EventParticipationAnswer.MAYBE) ? 'opacity-100' : 'absolute opacity-0'
          }`}>
          <button
            type="button"
            key={EventParticipationAnswer.MAYBE}
            className={`flex h-10 w-10 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 md:h-14 md:w-14 ${
              selectedAnswer === EventParticipationAnswer.MAYBE ? 'shadow-lg' : 'hover:scale-105'
            } ${shouldShowPill(EventParticipationAnswer.MAYBE) ? 'rounded-r-none' : 'rounded-full'}`}
            style={{ backgroundColor: COLOR_ALERT }}>
            <FaQuestion className="font-bold" />
          </button>
          <div
            className={`flex h-10 items-center rounded-r-full px-3 text-sm font-medium text-white transition-all duration-300 md:h-14 md:px-4 md:text-base ${
              shouldShowPill(EventParticipationAnswer.MAYBE)
                ? 'w-auto translate-x-0 opacity-70'
                : 'w-0 -translate-x-4 overflow-hidden opacity-0'
            }`}
            style={{ backgroundColor: COLOR_ALERT }}>
            {getButtonText(EventParticipationAnswer.MAYBE)}
          </div>
        </div>
        <button
          type="button"
          key={`${EventParticipationAnswer.MAYBE}-standalone`}
          onClick={() => handleButtonClick(EventParticipationAnswer.MAYBE)}
          className={`flex h-10 w-10 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 md:h-14 md:w-14 ${
            selectedAnswer === EventParticipationAnswer.MAYBE ? 'shadow-lg' : 'hover:scale-105'
          } ${shouldShowPill(EventParticipationAnswer.MAYBE) ? 'absolute opacity-0' : 'opacity-100'}`}
          style={{ backgroundColor: COLOR_ALERT }}>
          <Icon icon={FaQuestion} />
        </button>
      </div>

      {/* NO Button */}
      <div className="relative flex items-center">
        <div
          onClick={() => handleButtonClick(EventParticipationAnswer.NO)}
          className={`flex items-center transition-all duration-300 ${
            shouldShowPill(EventParticipationAnswer.NO) ? 'opacity-100' : 'absolute opacity-0'
          }`}>
          <button
            type="button"
            key={EventParticipationAnswer.NO}
            className={`flex h-10 w-10 items-center justify-center rounded-full text-secondary-50 duration-200 md:h-14 md:w-14 ${
              selectedAnswer === EventParticipationAnswer.NO ? 'shadow-lg' : 'hover:scale-105'
            } ${shouldShowPill(EventParticipationAnswer.NO) ? 'rounded-r-none' : 'rounded-full'}`}
            style={{ backgroundColor: COLOR_ERROR }}>
            <Icon icon={FaThumbsDown} />
          </button>
          <div
            className={`flex h-10 items-center rounded-r-full px-3 text-sm font-medium text-white transition-all duration-300 md:h-14 md:px-4 md:text-base ${
              shouldShowPill(EventParticipationAnswer.NO)
                ? 'w-auto translate-x-0 opacity-70'
                : 'w-0 -translate-x-4 overflow-hidden opacity-0'
            }`}
            style={{ backgroundColor: COLOR_ERROR }}>
            {getButtonText(EventParticipationAnswer.NO)}
          </div>
        </div>
        <button
          type="button"
          key={`${EventParticipationAnswer.NO}-standalone`}
          onClick={() => handleButtonClick(EventParticipationAnswer.NO)}
          className={`flex h-10 w-10 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 md:h-14 md:w-14 ${
            selectedAnswer === EventParticipationAnswer.NO ? 'shadow-lg' : 'hover:scale-105'
          } ${shouldShowPill(EventParticipationAnswer.NO) ? 'absolute opacity-0' : 'opacity-100'}`}
          style={{ backgroundColor: COLOR_ERROR }}>
          <Icon icon={FaThumbsDown} />
        </button>
      </div>
    </div>
  );
}
